/**
 * NDAA Section 889 compliance helpers.
 *
 * §889 of the FY 2019 National Defense Authorization Act bans U.S. federal
 * agencies (and federally-funded projects) from using video surveillance
 * equipment from specific covered entities and their subsidiaries.
 *
 * Filter logic — hybrid:
 *   1. Brand on banned list  → NOT compliant
 *   2. Explicit ndaaCompliant === false  → NOT compliant
 *   3. Everything else  → compliant (per the specific-entity nature of §889)
 */

import { getVisibleSpec } from '@/data/visibleSpecsIndex';

const NDAA_BANNED_BRANDS: ReadonlySet<string> = new Set([
  'hikvision',  // Hangzhou Hikvision Digital Technology
  'dahua',      // Dahua Technology
  'huawei',     // Huawei Technologies
  'hytera',     // Hytera Communications
  'zte',        // ZTE Corporation
  'hilook',     // Hikvision subsidiary
  'ezviz',      // Hikvision consumer brand
  'hiwatch',    // Hikvision value series (hikvision.com/products/hiwatch)
  'imou',       // Dahua consumer brand, launched by Dahua in 2018
]);

/** A covered entity named inside a longer brand string ("Hikvision HiWatch"). */
const NAMED_ENTITY = /\b(hikvision|dahua|huawei|hytera|zte)\b/;

export function isBrandNdaaBanned(brand: string | undefined | null): boolean {
  if (!brand) return false;
  const name = brand.toLowerCase().trim();
  return NDAA_BANNED_BRANDS.has(name) || NAMED_ENTITY.test(name);
}

export function isCameraNdaaCompliant(brand: string, modelName: string): boolean {
  if (isBrandNdaaBanned(brand)) return false;
  const spec = getVisibleSpec(modelName);
  if (spec?.spec.ndaaCompliant === false) return false;
  return true;
}

export function isModelExplicitlyNonCompliant(modelName: string): boolean {
  return getVisibleSpec(modelName)?.spec.ndaaCompliant === false;
}

export function isModelVerifiedNdaa(modelName: string): boolean {
  return getVisibleSpec(modelName)?.spec.ndaaCompliant === true;
}
